import type { BomItem, SupplyChainPlan } from '../types/supplyChain';

function nonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Runtime-safe definition of a reportable user BOM row. Price and quote data
 * are deliberately absent: those are reportable risks, not prerequisites.
 */
export function isReportableUserBomItem(item: BomItem): boolean {
  const userAuthored =
    item.derivedFrom === 'manual' || item.derivedFrom === 'uploaded-bom';
  const hasIdentity =
    typeof item.name === 'string' && item.name.trim().length > 0;
  const hasUsableSpecification =
    (typeof item.spec === 'string' && item.spec.trim().length > 0)
    || (
      typeof item.description === 'string'
      && item.description.trim().length > 0
    );
  const hasLeadTimeBasis =
    nonNegative(item.leadTimeMonthsLow)
    && nonNegative(item.leadTimeMonthsHigh)
    && item.leadTimeMonthsHigh >= item.leadTimeMonthsLow
    && (
      item.leadTimeSource === 'industry-baseline'
      || item.leadTimeSource === 'manual'
      || item.leadTimeSource === 'quote'
    );
  return (
    userAuthored
    && hasIdentity
    && hasUsableSpecification
    && typeof item.quantity === 'number'
    && Number.isFinite(item.quantity)
    && item.quantity > 0
    && typeof item.unit === 'string'
    && item.unit.trim().length > 0
    && hasLeadTimeBasis
    && (
      item.criticality === 'critical-path'
      || item.criticality === 'critical'
      || item.criticality === 'standard'
    )
  );
}

/**
 * Minimum user-authored equipment lines before Supply Chain Risk is decision-grade.
 *
 * ASSUMPTION (product threshold, 2026-09-03): a single BoM line is a note, not a
 * chosen equipment basis. Three reportable lines is the smallest set that can
 * express RELATIVE lead-time exposure between components, which is what the
 * analysis actually reasons about. Tune here — every surface reads this constant.
 */
export const SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS = 3;

/**
 * Criticality values that carry real schedule exposure. A plan made entirely of
 * `standard` commodity lines cannot support a procurement-risk conclusion, so it
 * is not yet an equipment basis for this analysis.
 */
function carriesScheduleExposure(item: BomItem): boolean {
  return item.criticality === 'critical-path' || item.criticality === 'critical';
}

/**
 * True when the user has a real, chosen equipment basis: at least
 * `SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS` uploaded or manual line items, of which
 * at least one carries schedule exposure. Quotes and suppliers enrich that
 * authority but never replace it; a quote attached to an intent-seeded template
 * row is still not a user-authored equipment plan.
 */
export function isSupplyChainPlanUserReady(plan: SupplyChainPlan | undefined): boolean {
  if (!plan) return false;
  const reportable = plan.bom.filter(isReportableUserBomItem);
  if (reportable.length < SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS) return false;
  return reportable.some(carriesScheduleExposure);
}

/** Legacy intent-seeded or ghost BoM rows — not user-authored data. */
export function isTemplateOnlySupplyChainPlan(plan: SupplyChainPlan | undefined): boolean {
  if (!plan || plan.bom.length === 0) return false;
  return plan.bom.every(
    b => b.derivedFrom === 'auto-template'
  );
}
